import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { HiOutlineTrash, HiOutlinePlus } from 'react-icons/hi';
import {
  createTeamMember,
  updateTeamMember,
  getAllVMInstances,
  getCloudRunServicesList,
  getCloudRunJobs,
  getTaskQueues,
  getCloudBuildTriggersLight,
  getSchedulerJobs,
  getBuckets,
  getServiceAccounts,
  getPubSubTopics,
} from '../services/api';
import selectStyles from '../shared/selectStyles';
import './AddTeamMemberModal.css';

const SERVICE_TYPES = [
  { value: 'cloud_run', label: 'Cloud Run Service' },
  { value: 'vm_instance', label: 'VM Instance' },
  { value: 'cloud_run_job', label: 'Cloud Run Job' },
  { value: 'cloud_task', label: 'Cloud Task Queue' },
  { value: 'cloud_build', label: 'Cloud Build Trigger' },
  { value: 'cloud_scheduler', label: 'Cloud Scheduler Job' },
  { value: 'bucket', label: 'GCS Bucket' },
  { value: 'service_account', label: 'Service Account' },
  { value: 'gemini_api', label: 'Gemini API' },
  { value: 'pub_sub', label: 'Pub/Sub Topic' },
];

const FETCHERS = {
  cloud_run: (pid) => getCloudRunServicesList(pid).then(r => r.data.map(s => ({ value: s.name, label: `${s.name} (${s.region})`, region: s.region }))),
  vm_instance: (pid) => getAllVMInstances(pid).then(r => r.data.map(v => ({ value: v.name, label: `${v.name} (${v.zone})`, region: v.zone }))),
  cloud_run_job: (pid) => getCloudRunJobs(pid).then(r => r.data.map(j => ({ value: j.name, label: `${j.name} (${j.region})`, region: j.region }))),
  cloud_task: (pid) => getTaskQueues(pid).then(r => r.data.map(q => ({ value: q.name, label: `${q.name} (${q.region})`, region: q.region }))),
  cloud_build: (pid) => getCloudBuildTriggersLight(pid).then(r => r.data.map(t => ({ value: t.name || t.id, label: t.region && t.region !== 'global' ? `${t.name || t.id} (${t.region})` : (t.name || t.id), region: t.region || '' }))),
  cloud_scheduler: (pid) => getSchedulerJobs(pid).then(r => r.data.map(j => ({ value: j.name, label: `${j.name} (${j.region})`, region: j.region }))),
  bucket: (pid) => getBuckets(pid).then(r => r.data.map(b => ({ value: b.name, label: `${b.name} (${b.location})`, region: b.location }))),
  service_account: (pid) => getServiceAccounts(pid).then(r => r.data.map(a => ({ value: a.email, label: a.email, region: '' }))),
  pub_sub: (pid) => getPubSubTopics(pid).then(r => r.data.map(t => ({ value: t.name, label: t.name, region: '' }))),
};

function AddTeamMemberModal({ gcpProjects, onClose, onCreated, editMember }) {
  const [name, setName] = useState(editMember?.name || '');
  const [savedServices, setSavedServices] = useState(editMember?.services || []);
  const [saving, setSaving] = useState(false);

  // Current "add row" state
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [geminiEndpoint, setGeminiEndpoint] = useState('');
  const [geminiModel, setGeminiModel] = useState('');

  const projectOptions = gcpProjects.map(p => ({
    value: p._id,
    label: `${p.projectName} (${p.projectId})`,
    projectName: p.projectName,
  }));

  const fetchServices = useCallback(async () => {
    if (!selectedProject || !selectedType) return;
    if (selectedType.value === 'gemini_api') return;
    setLoadingServices(true);
    setAvailableServices([]);
    try {
      const fetcher = FETCHERS[selectedType.value];
      if (fetcher) {
        const opts = await fetcher(selectedProject.value);
        const assignedForProject = new Set(
          savedServices
            .filter(s => s.type === selectedType.value && s.gcpProjectId === selectedProject.value)
            .map(s => s.name)
        );
        const filtered = opts
          .filter(o => !assignedForProject.has(o.value))
          .sort((a, b) => a.label.localeCompare(b.label));
        setAvailableServices(filtered);
      }
    } catch {
      toast.error(`Failed to fetch ${selectedType.label}`);
    }
    setLoadingServices(false);
  }, [selectedProject, selectedType, savedServices]);

  useEffect(() => {
    setAvailableServices([]);
    setSelectedServices([]);
    fetchServices();
  }, [fetchServices]);

  const handleAddServices = () => {
    if (!selectedProject || !selectedType) return;

    if (selectedType.value === 'gemini_api') {
      if (!geminiEndpoint.trim()) {
        toast.error('Gemini endpoint is required');
        return;
      }
      const entry = {
        type: 'gemini_api',
        name: geminiModel.trim() || 'Gemini API',
        region: '',
        gcpProjectId: selectedProject.value,
        gcpProjectName: selectedProject.projectName,
        meta: { endpoint: geminiEndpoint.trim(), model: geminiModel.trim() },
      };
      setSavedServices(prev => [...prev, entry]);
      setGeminiEndpoint('');
      setGeminiModel('');
      return;
    }

    if (selectedServices.length === 0) {
      toast.error('Select at least one service');
      return;
    }

    const newEntries = selectedServices.map(s => ({
      type: selectedType.value,
      name: s.value,
      region: s.region || '',
      gcpProjectId: selectedProject.value,
      gcpProjectName: selectedProject.projectName,
      meta: {},
    }));

    setSavedServices(prev => [...prev, ...newEntries]);
    setSelectedServices([]);
  };

  const removeService = (idx) => {
    setSavedServices(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Employee name is required');
      return;
    }
    setSaving(true);
    try {
      if (editMember) {
        const res = await updateTeamMember(editMember._id, { name: name.trim(), services: savedServices });
        toast.success(`Updated "${name}"`);
        onCreated(res.data);
      } else {
        const res = await createTeamMember({ name: name.trim(), services: savedServices });
        toast.success(`Team member "${name}" created`);
        onCreated(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save team member');
    }
    setSaving(false);
  };

  const typeLabel = (type) => SERVICE_TYPES.find(t => t.value === type)?.label || type;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal add-team-modal" onClick={e => e.stopPropagation()}>
        <h2>{editMember ? 'Edit Team Member' : 'Add Team Member'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Employee Name</label>
            <input
              type="text"
              placeholder="Enter employee name..."
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="team-add-row">
            <div className="team-add-row-title">
              <HiOutlinePlus size={14} />
              <span>Assign Services</span>
            </div>

            <div className="form-group">
              <label>GCP Project</label>
              <Select
                styles={selectStyles}
                options={projectOptions}
                value={selectedProject}
                onChange={(v) => { setSelectedProject(v); setSelectedType(null); }}
                placeholder="Select a project..."
                isClearable
              />
            </div>

            {selectedProject && (
              <div className="form-group">
                <label>Service Type</label>
                <Select
                  styles={selectStyles}
                  options={SERVICE_TYPES}
                  value={selectedType}
                  onChange={setSelectedType}
                  placeholder="Select a service type..."
                  isClearable
                />
              </div>
            )}

            {selectedProject && selectedType && selectedType.value === 'gemini_api' && (
              <>
                <div className="form-group">
                  <label>Endpoint</label>
                  <input
                    type="text"
                    placeholder="https://..."
                    value={geminiEndpoint}
                    onChange={e => setGeminiEndpoint(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Model Name (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. gemini-1.5-pro"
                    value={geminiModel}
                    onChange={e => setGeminiModel(e.target.value)}
                  />
                </div>
              </>
            )}

            {selectedProject && selectedType && selectedType.value !== 'gemini_api' && (
              <div className="form-group">
                <label>Select Services</label>
                {loadingServices ? (
                  <div className="loading-inline">
                    <div className="spinner" style={{ width: 20, height: 20 }}></div>
                    <span>Fetching {selectedType.label}...</span>
                  </div>
                ) : (
                  <Select
                    styles={selectStyles}
                    options={availableServices}
                    value={selectedServices}
                    onChange={setSelectedServices}
                    placeholder={`Select ${selectedType.label}...`}
                    isMulti
                    closeMenuOnSelect={false}
                    noOptionsMessage={() => `No ${selectedType.label} found`}
                  />
                )}
              </div>
            )}

            {selectedProject && selectedType && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddServices}>
                <HiOutlinePlus size={14} /> Add to List
              </button>
            )}
          </div>

          {savedServices.length > 0 && (
            <div className="team-assigned-list">
              <label>Assigned Services ({savedServices.length})</label>
              <div className="team-assigned-items">
                {savedServices.map((svc, idx) => (
                  <div key={idx} className="team-assigned-item">
                    <span className={`team-type-badge team-type-${svc.type}`}>{typeLabel(svc.type)}</span>
                    <span className="team-svc-name">{svc.name}</span>
                    <span className="team-svc-project">{svc.gcpProjectName}</span>
                    {svc.region && <span className="team-svc-region">{svc.region}</span>}
                    <button type="button" className="team-remove-btn" onClick={() => removeService(idx)}>
                      <HiOutlineTrash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>
              {saving ? 'Saving...' : editMember ? 'Update' : 'Create Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddTeamMemberModal;
